/**
 * The goal rows the Goals board manages, as the World door serves them
 * (GOALS-GOAL-MANAGEMENT GF1–GF3). `parent` is the stored parent; the fold
 * over goal.relationship.changed moves supplies `parentIds`, `dependsOn` and
 * `contextLinks` (GF1: one World identity across multiple parents, typed
 * dependencies, context links). `step` is the six-stage vocabulary the World
 * Board folds into columns; `state` is exactly open | closed. Nothing here is
 * stored twice — every derived shape is computed at read (DL4).
 */
export const GOAL_STEPS = ["comprehend", "conceive", "decide", "design", "build", "run"] as const;
export type GoalStep = (typeof GOAL_STEPS)[number];

export interface ManagedGoal {
  readonly goalId: string;
  readonly title: string;
  readonly statement: string;
  readonly cleoCan: string | null;
  readonly state: "open" | "closed";
  readonly step?: GoalStep | string;
  readonly parent: string | null;
  readonly parentIds?: ReadonlyArray<string>;
  readonly dependsOn?: ReadonlyArray<string>;
  readonly contextLinks?: ReadonlyArray<{
    readonly memberId: string;
    readonly reason: string;
    readonly source: "human" | "cleo";
    readonly author: string;
  }>;
}

export interface GoalDraft {
  readonly parentGoalId: string;
  readonly title: string;
  readonly statement: string;
  readonly cleoCan: string;
}

export type GoalRelation = "parent" | "depends_on" | "context";

/** A goal's parents through either wire shape (GF1: never trust one field alone). */
export const goalParentIds = (goal: ManagedGoal): ReadonlyArray<string> =>
  goal.parentIds ?? (goal.parent ? [goal.parent] : []);

/** The stored root of every World: Harmony (W07). */
export const HARMONY_ROOT_GOAL_ID = "goal-000";

/** The first-parent ancestry as titles, root first; cycle-safe. */
export function goalAncestry(
  goal: ManagedGoal,
  byId: ReadonlyMap<string, ManagedGoal>,
): ReadonlyArray<string> {
  const labels: string[] = [];
  const seen = new Set([goal.goalId]);
  let id = goalParentIds(goal)[0];
  while (id && !seen.has(id)) {
    seen.add(id);
    const parent = byId.get(id);
    if (!parent) break;
    labels.unshift(parent.title);
    id = goalParentIds(parent)[0];
  }
  return labels;
}
